import React, {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {NotificationManager} from 'react-notifications';
import {API_BASE, API_ENDPOINT} from '../config';
import {useAuth} from '../auth/AuthContext';

/**
 * Gestiunea imaginilor de prezentare (slideshow) pentru companiile userului logat.
 * Datele vin din GET /api/v1/me; upload/delete lovesc /api/v1/me/companies/{code}/media
 * (SonataMedia: galerie per companie).
 */
const CompanyDocuments = () => {
    const {auth, logout} = useAuth();
    const [companies, setCompanies] = useState([]);
    const [activeCode, setActiveCode] = useState(null);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [metaEdits, setMetaEdits] = useState({});
    const fileInputRef = useRef(null);

    const authHeader = {Authorization: `Bearer ${auth.token}`};

    const loadMe = useCallback(async () => {
        setLoading(true);
        try {
            const {data} = await axios.get(`${API_ENDPOINT}/v1/me`, {headers: authHeader});
            const list = data.companies || [];
            setCompanies(list);
            setActiveCode((prev) => prev && list.some(c => c.code === prev) ? prev : (list[0]?.code ?? null));
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
            } else {
                NotificationManager.error('Nu am putut încărca datele companiei.');
            }
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.token]);

    useEffect(() => {
        loadMe();
    }, [loadMe]);

    const activeCompany = companies.find(c => c.code === activeCode) || null;
    const mediaItems = activeCompany?.media || [];
    const MAX_GALLERY_MEDIA = 3;
    const atLimit = mediaItems.length >= MAX_GALLERY_MEDIA;

    const handleFiles = async (files) => {
        if (!activeCompany || !files || files.length === 0) {
            return;
        }
        const remaining = MAX_GALLERY_MEDIA - mediaItems.length;
        if (remaining <= 0) {
            NotificationManager.warning(`Se pot încărca maxim ${MAX_GALLERY_MEDIA} imagini per companie.`);
            return;
        }
        const filesToUpload = Array.from(files).slice(0, remaining);
        if (files.length > filesToUpload.length) {
            NotificationManager.warning(`Se pot încărca maxim ${MAX_GALLERY_MEDIA} imagini per companie — primele ${filesToUpload.length} au fost trimise.`);
        }
        setUploading(true);
        let ok = 0;
        for (const file of filesToUpload) {
            const form = new FormData();
            form.append('imageFile', file);
            try {
                await axios.post(
                    `${API_ENDPOINT}/v1/me/companies/${activeCompany.code}/media`,
                    form,
                    {headers: {...authHeader, 'Content-Type': 'multipart/form-data'}}
                );
                ok += 1;
            } catch (err) {
                NotificationManager.error(`Eroare la încărcarea "${file.name}".`);
            }
        }
        if (ok > 0) {
            NotificationManager.success(`${ok} imagine(i) încărcate.`);
            await loadMe();
        }
        setUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (item) => {
        if (!activeCompany || !window.confirm('Ștergi această imagine?')) {
            return;
        }
        try {
            await axios.delete(
                `${API_ENDPOINT}/v1/me/companies/${activeCompany.code}/media/${item.id}`,
                {headers: authHeader}
            );
            NotificationManager.success('Imagine ștearsă.');
            await loadMe();
        } catch (err) {
            NotificationManager.error('Nu am putut șterge imaginea.');
        }
    };

    const saveMeta = async (item) => {
        if (!activeCompany) {
            return;
        }
        const e = metaEdits[item.id] || {};
        const urlName = e.urlName ?? item.urlName ?? '';
        const urlLink = e.urlLink ?? item.urlLink ?? '';
        try {
            await axios.patch(
                `${API_ENDPOINT}/v1/me/companies/${activeCompany.code}/media/${item.id}`,
                {urlLink, urlName},
                {headers: authHeader}
            );
            NotificationManager.success('Link salvat.');
            await loadMe();
        } catch (err) {
            NotificationManager.error(err.response?.data?.error || 'Nu am putut salva link-ul.');
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <i className="fas fa-spinner fa-spin fa-2x text-muted"></i>
            </div>
        );
    }

    if (companies.length === 0) {
        return (
            <div className="callout callout-warning">
                <h5>Nicio companie asociată</h5>
                <p className="mb-0">Contul dvs. nu este asociat niciunei companii.</p>
            </div>
        );
    }

    return (
        <div>
            {companies.length > 1 && (
                <ul className="nav nav-tabs mb-3">
                    {companies.map(c => (
                        <li className="nav-item" key={c.code}>
                            <button
                                type="button"
                                className={`nav-link ${c.code === activeCode ? 'active' : ''}`}
                                onClick={() => setActiveCode(c.code)}
                            >
                                {c.name}
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <div
                className="card card-outline card-primary"
                onDrop={onDrop}
                onDragOver={(e) => e.preventDefault()}
            >
                <div className="card-header d-flex align-items-center">
                    <h3 className="card-title mb-0">
                        <i className="fas fa-images mr-2"></i>
                        Imagini de prezentare — {activeCompany?.name}
                    </h3>
                    <div className="card-tools ml-auto">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={uploading || atLimit}
                            title={atLimit ? `Maxim ${MAX_GALLERY_MEDIA} imagini per companie` : undefined}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {uploading
                                ? <><i className="fas fa-spinner fa-spin mr-1"></i> Se încarcă…</>
                                : <><i className="fas fa-upload mr-1"></i> Încarcă imagini</>}
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            style={{display: 'none'}}
                            onChange={(e) => handleFiles(e.target.files)}
                        />
                    </div>
                </div>

                <div className="card-body">
                    {mediaItems.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <i className="fas fa-cloud-upload-alt fa-3x mb-3 d-block"></i>
                            <p className="mb-1">Nicio imagine încă.</p>
                            <small>Trageți imagini aici sau folosiți butonul „Încarcă imagini”.</small>
                        </div>
                    ) : (<>
                        <small className={atLimit ? 'text-warning d-block mb-2' : 'text-muted d-block mb-2'}>
                            {mediaItems.length} / {MAX_GALLERY_MEDIA} imagini
                            {atLimit && ' — șterge o imagine pentru a încărca alta'}
                        </small>
                        <div className="row">
                            {mediaItems.map(item => (
                                <div className="col-6 col-md-4 col-lg-3 mb-3" key={item.id}>
                                    <div className="position-relative">
                                        <img
                                            src={`${API_BASE}${item.thumbUrl || item.contentUrl}`}
                                            alt=""
                                            className="img-fluid rounded"
                                            style={{
                                                width: '100%',
                                                height: '140px',
                                                objectFit: 'cover',
                                                border: '1px solid #dee2e6',
                                            }}
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm position-absolute"
                                            style={{top: '6px', right: '6px'}}
                                            title="Șterge"
                                            onClick={() => handleDelete(item)}
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control form-control-sm mt-1"
                                        placeholder="Nume link (buton)"
                                        maxLength={145}
                                        value={metaEdits[item.id]?.urlName ?? item.urlName ?? ''}
                                        onChange={(ev) => setMetaEdits(m => ({...m, [item.id]: {...m[item.id], urlName: ev.target.value}}))}
                                    />
                                    <input
                                        type="url"
                                        className="form-control form-control-sm mt-1"
                                        placeholder="https://…"
                                        maxLength={512}
                                        value={metaEdits[item.id]?.urlLink ?? item.urlLink ?? ''}
                                        onChange={(ev) => setMetaEdits(m => ({...m, [item.id]: {...m[item.id], urlLink: ev.target.value}}))}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm btn-block mt-1"
                                        onClick={() => saveMeta(item)}
                                    >
                                        <i className="fas fa-save mr-1"></i> Salvează link
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>)}
                </div>
            </div>
        </div>
    );
};

export default CompanyDocuments;
